import { AcademicYearDTO, CourseSetDTO, CustomerDTO, SiteDTO } from "@beable/roster-backend-client-v1";
import {
  Box,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Toolbar,
  Tooltip,
  Typography,
  lighten,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import React, { useEffect, useState } from "react";
import courseService from "../../../services/CourseService";
import SchoolSelector from "../../Common/Selectors/SchoolSelector";
import { getStyles } from "../styles";

interface CloneScheduleProps {
  customer: CustomerDTO;
  site: SiteDTO;
  academicYear: AcademicYearDTO;
}

interface CourseData {
  id: string;
  name: string;
  grades: string | undefined;
}

function createCourseData(
  id: string,
  name: string,
  grades: string | undefined
): CourseData {
  return { id, name, grades };
}

interface CourseHeadCell {
  id: keyof CourseData;
  label: string;
}

export default function CloneSchedule({
  customer,
  site,
  academicYear,
}: CloneScheduleProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [orgId, setOrgId] = useState<string>();
  const [copyToOrgId, setCopyToOrgId] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<CourseData[]>([]);
  const [courseSets, setCourseSets] = useState<CourseSetDTO[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [isCloning, setIsCloning] = useState(false);
 
  useEffect(() => {
    const fetchCourseSets = async () => {
      setIsLoading(true);
      const data = await courseService.getProvisionedContentCourses(
        site.id,
        orgId!,
        academicYear.academicYearId
      );
      if (data) {
        data.courseSets.forEach((courseSet, index) => {
          data.courseSets[index].courses = courseSet.courses.sort(
            (a, b) => a.sort - b.sort
          );
        });
        setCourseSets(data.courseSets);
        setRows(
          data.courseSets.map((c) =>
            createCourseData(c.id!, getTitle(c), c.grades)
          )
        );
      } else {
        setRows([]);
        setCourseSets([]);
      }
      setIsLoading(false);
    };

    if (orgId) {
      fetchCourseSets();
    }
  }, [site, orgId, academicYear.academicYearId]);

  const doCloneCourses = async () => {
    setIsCloning(true);
    const selectedCourseSets = courseSets.filter((c) => selected.includes(c.id!));
    const response = await courseService.cloneSchedule(site.id, orgId!, copyToOrgId!, selectedCourseSets, academicYear, site.timezone!);
    if (response) {
        alert("Schedule cloned.  Please allow several minutes for lessons to be assigned to users.");
    } else {
        alert("There was an error submitting your request.");
    }
    setIsCloning(false);
  }

  const handleChangeOrg = (orgId: string) => {
    if (orgId) {
      setIsLoading(true);
    }
    setSelected([]);
    setOrgId(orgId);
  };

  const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (id: string) => selected.includes(id);

  const getTitle = (courseSet: CourseSetDTO) => {
    if (courseSet.autoGenerated) {
      return courseSet.courses[0].courseName + " (Course)";
    } else {
      return courseSet.name + " (Course Set)";
    }
  };

  const headCells: CourseHeadCell[] = [
    { id: "name", label: "Course / Course Set" },
    { id: "grades", label: "Grades" },
  ];

  const getToolbarStyles = (theme: Theme) => ({
    root: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
    },
    highlight:
      theme.palette.mode === "light"
        ? {
            color: theme.palette.secondary.main,
            backgroundColor: lighten(theme.palette.secondary.light, 0.85),
          }
        : {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.secondary.dark,
          },
    title: {
      flex: "1 1 100%",
    },
  });

  const EnhancedToolbar = () => {
    const toolbarStyles = getToolbarStyles(theme);
    const isDisabled = !copyToOrgId || !orgId || isCloning;

    const adjustedButtonProps = {
        disabled: isDisabled,
        component: isDisabled ? "div" : undefined,
        onClick: isDisabled ? undefined : doCloneCourses
    };

    return (
      <Toolbar
        sx={[toolbarStyles.root, !!selected.length && toolbarStyles.highlight]}
      >
        {!!selected.length && (
          <Typography
            variant="subtitle1"
            component="div"
            sx={[{
              color: "inherit"
            }, toolbarStyles.title]}>
            {selected.length} selected
          </Typography>
        )}
        {!!selected.length && (
          <Tooltip title="Clone Schedule">
            <IconButton aria-label="clone" {...adjustedButtonProps} size="large">
              <FileCopyIcon />
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
    );
  };
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };
  const EnhancedTableHead = () => {
    return (
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={
                selected.length > 0 && selected.length < rows.length
              }
              checked={rows.length > 0 && selected.length === rows.length}
              onChange={handleSelectAll}
              slotProps={{ input: { "aria-label": "select all course sets / courses" } }}
            />
          </TableCell>
          {headCells.map((headCell) => (
            <TableCell
              key={headCell.id}
              align="left"
              padding="normal"
              sx={{ fontWeight: 'bold' }}
            >
              {headCell.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };
  return (
    <Box sx={styles.schedule_root}>
      <Grid container>
        <Grid sx={styles.nudgedSelectlist} size={4}>
            <SchoolSelector
            siteId={site ? site.id : 0}
            onChange={(schoolId: string) => handleChangeOrg(schoolId)}
            variant="outlined"
            />
        </Grid>
        <Grid size={4} />
        <Grid size={4}>
            <SchoolSelector
                siteId={site ? site.id : 0}
                onChange={(schoolId: string) => setCopyToOrgId(schoolId)}
                disabled={!orgId}
                omitSchool={orgId}
                label={"Clone to school"}
                variant="outlined"
            />
        </Grid>
      </Grid>
      {!orgId ? null :
      !isLoading && orgId && (!rows || rows.length < 1) ? (
        <Typography>No provisioned content courses found</Typography>
      ) : isLoading ? (
        <Box sx={styles.loaderContainer}>
          <CircularProgress disableShrink />
        </Box>
      ) : (
        <>
          <EnhancedToolbar />
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <EnhancedTableHead />
              <TableBody>
                {rows &&
                  rows.map((row, index) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;
                    return (
                      <TableRow
                        hover
                        onClick={(event) => handleClick(event, row.id)}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.id}
                        selected={isItemSelected}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) =>
                              handleClick(
                                event as unknown as React.MouseEvent<unknown>,
                                row.id
                              )
                            }
                            slotProps={{ input: { "aria-labelledby": labelId } }}
                          />
                        </TableCell>
                        <TableCell
                          component="th"
                          id={labelId}
                          scope="row"
                          padding="none"
                        >
                          {row.name}
                        </TableCell>
                        <TableCell align="right">{row.grades}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}